/**
 * « Coup de pouce » — option d'aide pendant la passation. V1 = UI SEULE :
 * aucun indice réel n'est généré, aucun coût n'est débité (le back reste
 * source de vérité). Flag OFF par défaut → zéro impact en production ;
 * activation pour démo/dev : localStorage.setItem('tanoCoupDePouce', '1').
 */

import COUPS_DE_POUCE from '../generators/coupsDePouce';

const FLAG_KEY = 'tanoCoupDePouce';

export const isCoupDePouceEnabled = () => {
    try { return localStorage.getItem(FLAG_KEY) === '1'; } catch { return false; }
};

// Pas de « coût » en monnaie : utiliser un coup de pouce RÉDUIT l'XP gagnée si
// la réponse est juste (XP_BONNE → XP_BONNE_AIDE, cf. xpConfig). Arbitrage
// pédagogique : l'aide n'est jamais une punition, juste une récompense réduite.

/**
 * Indice d'une question, résolu ENTIÈREMENT côté front.
 *
 * Clé = couple (attendu, generator_key). `generator_key` est persisté par le
 * back (alimenté depuis _meta.generatorKey au POST) : il identifie le FORMAT
 * d'exercice, pas le tirage. L'indice est donc attaché au format — c'est
 * voulu : un indice calqué sur le tirage donnerait la réponse.
 *
 * Contrat de retour :
 *   null                            → conseil générique de Tano (pas d'indice écrit)
 *   'texte court'                   → indice d'une phrase (page unique)
 *   { court, svg?, exemple? }       → propriété (+ figure) en page 1, exemple en page 2
 *                                     `exemple` : string ou tableau de lignes
 *                                     emphase : **texte** (gras) ou ==texte==
 *                                     (gras rouge) ; $...$ rendu en KaTeX
 *                                     inline (vraies fractions) — jamais de
 *                                     HTML brut ni de tiret cadratin
 *
 * Forme à VARIANTES (formats multi-contextes ou multi-calculs) :
 *   { variantes: [{ motif?, sousType?, pages }, …], defaut: { pages } }
 * Deux routages possibles, du plus robuste au plus contextuel :
 *   - `sousType` (ex. 650) : comparé à question.contenu.sousType, un champ
 *     STRUCTUREL posé par la branche du gen qui a choisi le type de calcul —
 *     insensible aux reformulations d'énoncés (canal contenu, survit au POST) ;
 *   - `motif` (ex. 643) : mot-clé cherché dans le texte de la question
 *     (exclusif au contexte, vérifié par le harnais du gen).
 * Filet : rien ne matche (ancienne question sans sousType, énoncé
 * reformulé) → `defaut`, l'aide générique — jamais de crash, jamais de
 * sheet vide.
 */
export const getIndice = (question) => {
    const attendu = question?.attendu;
    // `generator_key` : question relue du back (passation). `_meta.generatorKey` :
    // payload sortant tout juste du générateur (aperçu élève côté prof, défi
    // quotidien) — jamais persisté, donc absent du premier champ.
    const key = question?.generator_key || question?._meta?.generatorKey;
    if (!attendu || !key) return null;
    const entree = COUPS_DE_POUCE[attendu]?.[key] || null;
    if (entree?.variantes) {
        const texte = String(question?.texte || '');
        let contenu = question?.contenu;
        if (typeof contenu === 'string') {
            try { contenu = JSON.parse(contenu); } catch { contenu = null; }
        }
        const sousType = contenu && typeof contenu === 'object' ? contenu.sousType : undefined;
        const v = entree.variantes.find((va) => (
            va.sousType ? va.sousType === sousType : (va.motif ? texte.includes(va.motif) : false)
        ));
        return v || entree.defaut || null;
    }
    return entree;
};
